import React, { useState, useEffect, useContext, useCallback } from "react";
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { useWeb3React } from "@web3-react/core";
import ContractContext from '../../ContractContext';
import { InputNumber } from 'primereact/inputnumber';
import { Dialog } from 'primereact/dialog';
import { formatCurrencyExtended, formatDecimals, getTokenLogo, formatBalanceWithIcon, formatUSDBalanceWithIcon } from '../../utils';
import floatieswap_logo from "../../imgs/floatieswap_logo.png";
import stakehause_logo from "../../imgs/stakehause_logo.png";
import burnlab_logo from "../../imgs/burnlab_logo.png";
import earnpay_logo from "../../imgs/earnpay_logo.png";
import ierc20Abi from '../../contracts/ierc20Abi';
import Web3 from "web3";
import BigNumber from 'bignumber.js';
import { infoOutlineStyle, miniTokenCardStyle } from './TileStyles';
import TokenSelectorModal from './TokenSelectorModal';
import TokenInput from './TokenInput';
import RewardsCard from './RewardsCard';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { TabView, TabPanel } from 'primereact/tabview';
import { Tab } from "@mui/material";
import { Card as NextCard, Switch } from "@nextui-org/react";
import PlayPlatformModal from './PlayPlatformModal';
import TokenCarousel from "./TokenCarousel";
import TransactionToast from "../TransactionToast";
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';  // USD icon
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import AttractionsIcon from '@mui/icons-material/Attractions';

import { toast } from 'react-toastify';
import { padding } from "@mui/system";

const LoadingContent = () => <Skeleton width="100%" height="35px" className="mb-2" />;

const containerStyle = {
    maxWidth: '1200px', // Adjust this value according to your preference
    margin: '0 auto', // Centers the container
    width: '100%', // Takes full width on smaller screens
    padding: '0 10px', // Optional padding for some spacing from the edges
};


const skeletonCardStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignContent: 'start',
    maxWidth: '200px',
    minWidth: '200px',
    padding: '12px',
    backgroundColor: '#f0f0f0', // Light gray color for the skeleton
    border: '2px solid #e0e0e0', // Light border
    borderRadius: '25px',
    maxHeight: '110px',
    color: 'transparent', // Hide text
    animation: 'pulse 1.5s infinite', // Add pulse animation
    //outline: '2px solid #EC599A', // Gradient from EC599A to 0066A4
    marginTop: '20px',
    marginBottom: '20px',
};

const SkeletonCard = React.memo(() => {


    return (
        <>
            <NextCard style={skeletonCardStyle}>
                <NextCard.Body style={{ padding: '3px', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold', fontSize: '18px' }}>
                        <div style={{ width: '30px', height: '30px', backgroundColor: '#e0e0e0', borderRadius: '50%' }} />
                        <LoadingContent />
                    </div>
                    <div style={{ marginTop: '10px', fontSize: '14px', fontWeight: 'normal' }}>
                        <div style={{ width: '100%', height: '20px', backgroundColor: '#e0e0e0', marginBottom: '5px' }}></div>
                        <LoadingContent />
                    </div>
                </NextCard.Body>
            </NextCard>
        </>
    );
});


const getRatingIcon = (poolIndex) => {
    switch (poolIndex) {
        case 0:
            return '🥇'
        case 1:
            return '🥈'
        case 2:
            return '🥉'
        case 3:
            return '🫕'
        default: return '🎖️'
    }
};

const Stake = React.memo(({ tokenSelections, refreshTokenSelections, imageReferences }) => {
    const { account, library } = useWeb3React();
    const contractContext = useContext(ContractContext);
    const contracts = contractContext.contracts;

    const [tokenIn, setTokenIn] = useState(null);
    const [tokenInBalance, setTokenInBalance] = useState(0);
    const [amountIn, setAmountIn] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [filteredTokens, setFilteredTokens] = useState([]);
    const [txPending, setTxPending] = useState(false);

    const [userStakedTokens, setUserStakedTokens] = useState([]);
    const [allowance, setAllowance] = useState(new BigNumber(0));

    const [showPlayPlatformModal, setShowPlayPlatformModal] = useState(false);
    const [selectedPlayPlatform, setSelectedPlayPlatform] = useState(null);
    const [selectedToken, setSelectedToken] = useState(null);
    const [usdView, setUsdView] = useState(() => localStorage.getItem('usdView') === 'true');
    const [detailView, setDetailView] = useState(() => localStorage.getItem('detailView') === 'true');

    // Function to show a dynamic toast
    const showDynamicToast = (type, message, options = {}) => {
        switch (type) {
            case 'info':
                return toast.info(message, { ...options, autoClose: false });
            case 'success':
                return toast.success(message, { ...options, autoClose: 3000 });
            case 'error':
                return toast.error(message, { ...options, autoClose: 5000 });
            case 'warning':
                return toast.warn(message, { ...options, autoClose: 5000 });
            default:
                return toast(message, { ...options, autoClose: 5000 });
        }
    };

    const handleTokenSelectByType = useCallback((type, token) => {
        if (type === 'in') {
            setTokenIn(token);
            setAmountIn('');
            localStorage.setItem('tokenInStake', token.address);
        }
    }, []);

    const fetchPlayPlatform = useCallback(async (token) => {
        try {
            const playPlatform = await contracts.floatiePlay.methods.playPlatform(token.address, 3).call();
            setSelectedPlayPlatform(playPlatform);
        } catch (error) {
            console.error("Error fetching play platform:", error);
        }
    }, [contracts.floatiePlay]);

    const handlePlayPlatformClick = useCallback(async (token, poolIndex) => {
        try {
            await fetchPlayPlatform(token);
            setSelectedToken(token);
            setShowPlayPlatformModal(true);
        } catch (error) {
            console.error("Error fetching play platform:", error);
        }
    }, [fetchPlayPlatform]);

    const handleClaimAllRewards = async (token) => {
        try {
            setTxPending(true); // Disable buttons

            // Show initial loading toast
            const toastId = toast.loading("Claiming rewards...");
            const stakeRewards = true;
            await contracts.floatieGateway.methods.claimAllRewards(token.address, stakeRewards).send({ from: account })
                .on('transactionHash', (hash) => {
                    // Update the toast with transaction hash and keep the spinner
                    toast.update(toastId, {
                        render: <TransactionToast hash={hash} transactionType="Claiming Rewards" />, // Pass transaction
                        type: "info",
                        isLoading: true, // Keep the spinner
                        autoClose: false, // Prevent auto-close
                        closeButton: true,
                    });
                })
                .on('receipt', (receipt) => {
                    // Update the toast to success and remove the spinner
                    toast.update(toastId, {
                        render: "Rewards claimed successfully!",
                        type: "success",
                        isLoading: false, // Remove spinner
                        autoClose: 3000, // Auto close after 3 seconds
                        closeButton: true,
                    });
                    setTxPending(false); // Enable buttons
                })
                .on('error', (error) => {
                    // Update the toast to error and remove the spinner
                    toast.update(toastId, {
                        render: `Claiming rewards failed: ${error.message}`,
                        type: "error",
                        isLoading: false, // Remove spinner
                        autoClose: 5000, // Auto close after 5 seconds
                        closeButton: true,
                    });
                    setTxPending(false); // Enable buttons
                });

            fetchStakedTokens();
        } catch (error) {
            console.error('Claiming rewards failed:', error);
            //showDynamicToast('error', `The transaction has failed. ${error.message}`);
            setTxPending(false); // Enable buttons
        }
    };


    const handleUnstake = async (token, amount) => {
        try {
            setTxPending(true); // Disable buttons

            // Correctly calculating the amount with decimals
            const amountWithDecimals = new BigNumber(amount).toFixed().toString();

            // Show initial loading toast
            const toastId = toast.loading("Unstaking tokens...");
            const transferAllPendingRewards = false;
            const unstakeAll = true;
            //function unstake(IERC20 token, uint256 amount, bool transferAllPendingRewards, bool unstakeAll)
            await contracts.floatieGateway.methods.unstake(token.address, amountWithDecimals, transferAllPendingRewards, unstakeAll).send({ from: account })
                .on('transactionHash', (hash) => {
                    // Update the toast with transaction hash and keep the spinner
                    toast.update(toastId, {
                        render: <TransactionToast hash={hash} transactionType="Unstaking" />, // Pass transaction
                        type: "info",
                        isLoading: true, // Keep the spinner
                        autoClose: false, // Prevent auto-close
                        closeButton: true,
                    });
                })
                .on('receipt', (receipt) => {
                    // Update the toast to success and remove the spinner
                    toast.update(toastId, {
                        render: "Tokens unstaked successfully!",
                        type: "success",
                        isLoading: false, // Remove spinner
                        autoClose: 3000, // Auto close after 3 seconds
                        closeButton: true,
                    });
                    setTxPending(false); // Enable buttons
                })
                .on('error', (error) => {
                    // Update the toast to error and remove the spinner
                    toast.update(toastId, {
                        render: `Unstaking failed: ${error.message}`,
                        type: "error",
                        isLoading: false, // Remove spinner
                        autoClose: 5000, // Auto close after 5 seconds
                        closeButton: true,
                    });
                    setTxPending(false); // Enable buttons
                });

            fetchStakedTokens();
        } catch (error) {
            console.error('Unstaking failed:', error);
            //showDynamicToast('error', `The transaction has failed. ${error.message}`);
            setTxPending(false); // Enable buttons
        }
    };








    const handleQuickStakePlay = async (token, poolSettings) => {
        try {
            setTxPending(true); // Disable buttons

            // Correctly calculating the amount with decimals
            const amountWithDecimals = new BigNumber(poolSettings.requiredAmount).toFixed().toString();

            // Fetch the current allowance again to ensure it's up-to-date
            await fetchAllowance(token);

            if (allowance.lt(amountWithDecimals)) {
                // Reuse the same approval logic used in the regular staking
                await approveToken(token, amountWithDecimals);
            } else {
                // Perform stake logic
                // Show initial loading toast
                const toastId = toast.loading("Staking tokens...");

                await contracts.floatieGateway.methods.stakePlay(token.address, amountWithDecimals).send({ from: account })
                    .on('transactionHash', (hash) => {
                        // Update the toast with transaction hash and keep the spinner
                        toast.update(toastId, {
                            render: <TransactionToast hash={hash} transactionType="Staking" />, // Pass transactionType
                            type: "info",
                            isLoading: true, // Keep the spinner
                            autoClose: false, // Prevent auto-close
                            closeButton: true,
                        });
                    })
                    .on('receipt', (receipt) => {
                        // Update the toast to success and remove the spinner
                        toast.update(toastId, {
                            render: "Tokens staked successfully!",
                            type: "success",
                            isLoading: false, // Remove spinner
                            autoClose: 3000, // Auto close after 3 seconds
                            closeButton: true,
                        });
                        setTxPending(false); // Enable buttons
                    })
                    .on('error', (error) => {
                        // Update the toast to error and remove the spinner
                        toast.update(toastId, {
                            render: `Stake failed: ${error.message}`,
                            type: "error",
                            isLoading: false, // Remove spinner
                            autoClose: 5000, // Auto close after 5 seconds
                            closeButton: true,
                        });
                        setTxPending(false); // Enable buttons
                    });

                // Clean up
                refreshTokenSelections();
                fetchStakedTokens();
                fetchPlayPlatform(token);
            }

        } catch (error) {
            console.error('Stake failed:', error);
            showDynamicToast('error', `The transaction has failed. ${error.message}`);
            setTxPending(false); // Enable buttons
        }
    };

    const fetchAllowance = useCallback(async (token) => {
        try {
            const web3 = new Web3(library.provider);
            const tokenContract = new web3.eth.Contract(ierc20Abi, token.address);
            const allowance = await tokenContract.methods.allowance(account, contracts.floatieGateway._address).call();
            setAllowance(new BigNumber(allowance));
        } catch (error) {
            console.error("Error fetching allowance:", error);
        }
    }, [account, library, contracts.floatieGateway]);

    const approveToken = useCallback(async (token, amountWithDecimals) => {
        try {
            setTxPending(true); // Disable buttons
            const web3 = new Web3(library.provider);
            const tokenContract = new web3.eth.Contract(ierc20Abi, token.address);

            // Show initial loading toast
            const toastId = toast.loading("Approving token...");

            await tokenContract.methods.approve(contracts.floatieGateway._address, amountWithDecimals).send({ from: account })
                .on('transactionHash', (hash) => {
                    // Update the toast with transaction hash and keep the spinner
                    toast.update(toastId, {
                        render: <TransactionToast hash={hash} transactionType="Approval" />, // Pass transactionType
                        type: "info",
                        isLoading: true, // Keep the spinner
                        autoClose: false, // Prevent auto-close
                        closeButton: true,
                    });
                })
                .on('receipt', (receipt) => {
                    // Update the toast to success and remove the spinner
                    toast.update(toastId, {
                        render: "Token approved successfully!",
                        type: "success",
                        isLoading: false, // Remove spinner
                        autoClose: 3000, // Auto close after 3 seconds
                        closeButton: true,
                    });
                    setTxPending(false); // Enable buttons
                })
                .on('error', (error) => {
                    // Update the toast to error and remove the spinner
                    toast.update(toastId, {
                        render: `Approval failed: ${error.message}`,
                        type: "error",
                        isLoading: false, // Remove spinner
                        autoClose: 5000, // Auto close after 5 seconds
                        closeButton: true,
                    });
                    setTxPending(false); // Enable buttons
                });

            await fetchAllowance(token); // Fetch updated allowance after approval
        } catch (error) {
            console.error("Error approving token:", error);
            //showDynamicToast('error', `Approval Error: ${error.message}`);
            setTxPending(false); // Enable buttons
        }
    }, [account, library, contracts.floatieGateway, fetchAllowance]);

    const fetchStakedTokens = useCallback(async () => {
        try {
            const totalSharesData = await Promise.all(
                tokenSelections
                    .filter(token => token.balanceOfHolder > 0)
                    .map(async (token) => {
                        const totalShares = new BigNumber(await contracts.floatieStake.methods.getTotalShares(token.address).call());
                        const userShares = await contracts.floatieStake.methods.getShareHolder(token.address, account).call();
                        const userStakedBalance = new BigNumber(userShares.amount);
                        const unclaimed = (new BigNumber(await contracts.floatieStake.methods.getUnclaimedEarnings(token.address, account).call()))
                            .plus(new BigNumber(userShares.totalRealisedUndeposited));

                        console.log(`Token: ${token.label}, Total Shares: ${totalShares.toString()}, User Staked: ${userStakedBalance.toString()}`);

                        return {
                            token,
                            totalShares,
                            userStakedBalance: userStakedBalance.isNaN() ? new BigNumber(0) : userStakedBalance,
                            unclaimed,
                            userShares,
                        };
                    })
            );

            console.log("Total Shares Data: ", totalSharesData);

            const filteredTokensWithActiveStakes = totalSharesData.filter(({ totalShares, userStakedBalance }) => totalShares.gt(0) && userStakedBalance.gt(0));
            console.log("Filtered Tokens with Active Stakes: ", filteredTokensWithActiveStakes);

            setUserStakedTokens(filteredTokensWithActiveStakes);

        } catch (error) {
            console.error("Error fetching staked tokens:", error);
        }
    }, [tokenSelections?.length, contracts.floatieStake, account]);

    const getTokenLogoUrl = (token) => {
        return getTokenLogo(imageReferences, token);
    };

    const getTokenBalance = useCallback(async (token) => {
        try {
            if (!library?.provider || !token) return 0;
            const web3 = new Web3(library.provider);
            if (token.label === 'WBNB') {
                const balance = await web3.eth.getBalance(account);
                return balance;
            } else {
                const contract = new web3.eth.Contract(ierc20Abi, token.address);
                const balance = await contract.methods.balanceOf(account).call();
                return balance;
            }
        } catch (error) {
            console.error("Error fetching token balance:", error);
            return 0;
        }
    }, [account, library]);

    const [playPlatform, setPlayPlatform] = useState(null);


    const getTokenPlayPlatformByIndex = useCallback(async (token, poolIndex) => {
        // playPlatform structs
        // struct WinnerInfo {
        //     uint256 previousWonAmount;
        //     uint256 previousWinTime;
        //     address previousWinner;
        // }

        // struct PoolSettings {
        //     uint256 chance;
        //     uint256 accuracy;
        //     uint256 rewardRate; //to be divided by 10000
        //     uint256 requiredAmount;
        //     uint256 threshold;
        //     uint256 coolDownTime;
        //     uint256 minimumHoldAmount;
        // }

        // struct PlayPlatform {
        //     uint256 total;
        //     uint256 lastRoll;
        //     PoolSettings poolSettings;
        //     WinnerInfo winnerInfo;
        // } 
        try {
            if (!contracts.floatiePlay) return;
            const playPlatform = await contracts.floatiePlay.methods.playPlatform(token.address, poolIndex).call();
            setPlayPlatform(playPlatform);
        } catch (error) {
            console.error("Error fetching play platform:", error);
        }
    }, [contracts.floatiePlay]);

    const handleStake = async () => {
        console.log("Staking Token and Amount: ", tokenIn.address, amountIn);
        try {
            setTxPending(true); // Disable buttons
            const amountWithDecimals = new BigNumber(amountIn).toFixed().toString();

            // Show initial loading toast with spinner
            const toastId = toast.loading("Staking tokens...");

            await contracts.floatieGateway.methods.stakePlay(tokenIn.address, amountWithDecimals).send({ from: account })
                .on('transactionHash', (hash) => {
                    // Update the toast with transaction hash and keep the spinner
                    toast.update(toastId, {
                        render: <TransactionToast hash={hash} transactionType="Staking" />, // Pass transactionType
                        type: "info",
                        isLoading: true, // Keep the spinner
                        autoClose: false, // Prevent auto-close
                        closeButton: true,
                    });
                })
                .on('receipt', (receipt) => {
                    // Update the toast to success and remove the spinner
                    toast.update(toastId, {
                        render: "Tokens staked successfully!",
                        type: "success",
                        isLoading: false, // Remove spinner
                        autoClose: 3000, // Auto close after 3 seconds
                        closeButton: true,
                    });
                    setTxPending(false); // Enable buttons
                    setAmountIn(''); // Reset the amountIn to empty
                })
                .on('error', (error) => {
                    // Update the toast to error and remove the spinner
                    toast.update(toastId, {
                        render: `Stake failed: ${error.message}`,
                        type: "error",
                        isLoading: false, // Remove spinner
                        autoClose: 5000, // Auto close after 5 seconds
                        closeButton: true,
                    });
                    setTxPending(false); // Enable buttons
                });

            refreshTokenSelections();
        } catch (error) {
            console.error('Transaction failed:', error);
            //showDynamicToast('error', `The transaction has failed. ${error.message}`);
            setTxPending(false); // Enable buttons
        }
    };

    const handleTokenClick = useCallback(() => {
        setShowModal(true);
        setFilteredTokens(tokenSelections.filter(t => t.address.toLowerCase() !== tokenIn?.address.toLowerCase()));
    }, [tokenIn, tokenSelections]);

    const handleTokenSelect = useCallback((token) => {
        setTokenIn(token);
        setAmountIn(''); // Reset the amountIn to empty
        setShowModal(false);
        localStorage.setItem('tokenInStake', token.address);
    }, []);

    const handleCloseModal = useCallback(() => {
        setShowModal(false);
    }, []);

    useEffect(() => {
        if (tokenIn && tokenIn.address) {
            getTokenBalance(tokenIn).then(balance => setTokenInBalance(balance));
            fetchAllowance(tokenIn);
        } else {
            setTokenInBalance(0);
        }
    }, [tokenIn, account, library, getTokenBalance, fetchAllowance]);

    useEffect(() => {
        if (!tokenSelections || tokenSelections.length === 0) return;
        const storedTokenIn = localStorage.getItem('tokenInStake');

        const foundTokenIn = tokenSelections.find(token => token.address === storedTokenIn);

        if (foundTokenIn) {
            setTokenIn(foundTokenIn);
        } else {
            // Fallback to default tokens if not found in storage or token list
            const defaultTokenIn = tokenSelections.find(token => token.address === '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c');
            setTokenIn(defaultTokenIn);
        }
    }, [tokenSelections?.length]);

    // useEffect(() => {
    //     return () => {
    //         setUserStakedTokens([]); // Clear the state when the component is unmounted or switching tabs
    //     };
    // }, []);

    useEffect(() => {
        if (account && contracts.floatieStake) {
            fetchStakedTokens();
        }
    }, [account, contracts.floatieStake, tokenSelections, fetchStakedTokens]);

    useEffect(() => {
        usdView ? localStorage.setItem('usdView', 'true') : localStorage.setItem('usdView', 'false');
    }, [usdView]);

    useEffect(() => {
        detailView ? localStorage.setItem('detailView', 'true') : localStorage.setItem('detailView', 'false');
    }, [detailView]);

    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Function to determine if the screen is in desktop mode
    const isDesktop = () => windowWidth >= 768; // Adjust the breakpoint as needed

    return (
        <div style={{
            backgroundImage: 'linear-gradient(315deg, #E5F3FF 0%, #E5F3FF 0%)',
            borderRadius: '25px',
            paddingBottom: '10px',
            paddingTop: '5px',
            marginTop: '0px',
            marginBottom: '10px',
            overflowX: 'hidden',
        }}>
            <div style={containerStyle}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    ...infoOutlineStyle,
                    paddingTop: '0px',
                    paddingBottom: '10px',
                    paddingLeft: '5px',
                    paddingRight: '5px',
                }}>
                    <img src={stakehause_logo} alt="stakehause logo" style={{ maxWidth: '250px', maxHeight: '50px', marginBottom: '15px', marginTop: '25px', paddingTop: '5px' }} />
                </div>

                <TabView>
                    <TabPanel header="Menu">
                        <div style={{
                            marginRight: isDesktop() ? '-50%' : '-40px',
                            marginLeft: isDesktop() ? '-50%' : '-40px',
                            marginBottom: '20px',
                        }}>
                            {tokenSelections?.length > 0 ? (
                                <TokenCarousel
                                    tokens={tokenSelections}
                                    selectedTokenIn={tokenIn}
                                    onSelectToken={handleTokenSelectByType}  // Ensure this only updates `tokenIn`
                                    getTokenLogoUrl={getTokenLogoUrl}
                                    selectionType="in"  // Always set tokenIn for stake
                                    poolIndex={3} // Pass the relevant pool index for Staking
                                />
                            ) : (
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <SkeletonCard />
                                    <SkeletonCard />
                                    <SkeletonCard />
                                    <SkeletonCard />
                                    <SkeletonCard />
                                    <SkeletonCard />
                                    <SkeletonCard />
                                    <SkeletonCard />
                                    <SkeletonCard />
                                    <SkeletonCard />
                                    <SkeletonCard />
                                </div>
                            )}
                        </div>
                        <div style={{ marginTop: '15px' }}>
                            <TokenInput
                                onSelectToken={handleTokenClick}
                                token={tokenIn}
                                balance={tokenInBalance}
                                onAmountChange={setAmountIn}
                                amountIn={amountIn}
                                getTokenLogoUrl={getTokenLogoUrl}
                            />
                        </div>
                        <RewardsCard
                            token={tokenIn}
                            imageReferences={imageReferences}
                            getTokenLogoUrl={getTokenLogoUrl}
                            poolIndex={3} // Pass the relevant pool index for Staking
                            poolType="Stake"
                        />
                        {allowance.lt(new BigNumber(amountIn)) ? (
                            <Button
                                label="Approve"
                                onClick={() => approveToken(tokenIn, new BigNumber(amountIn).toFixed().toString())}
                                disabled={!tokenIn || !amountIn || amountIn <= 0 || txPending}
                                severity="info"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    height: '50px',
                                    borderRadius: '25px',
                                    marginTop: '15px',
                                    marginBottom: '15px',
                                }}
                            />
                        ) : (
                            <Button
                                label="Stake"
                                onClick={handleStake}
                                disabled={!tokenIn || !amountIn || amountIn <= 0 || allowance.lt(new BigNumber(amountIn)) || txPending}
                                severity="warning"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    height: '50px',
                                    borderRadius: '25px',
                                    marginTop: '15px',
                                    marginBottom: '15px',
                                }}
                            />
                        )}
                        {showModal && (
                            <TokenSelectorModal
                                tokens={filteredTokens}
                                onSelect={handleTokenSelect}
                                onClose={handleCloseModal}
                                isVisible={showModal}
                                setShowModal={setShowModal}
                                getTokenLogoUrl={getTokenLogoUrl}
                                formatBalanceWithLogo={formatBalanceWithIcon}
                                poolIndex={3} // Pass the relevant pool index for Stake
                                poolType="Stake"
                            />
                        )}
                    </TabPanel>
                    <TabPanel header="Your Stake Platter">
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '5px' }}>
                            <Switch
                                checked={detailView}
                                size="md"
                                onChange={() => setDetailView(!detailView)}
                                iconOn={<RocketLaunchIcon />}
                                iconOff={<AttractionsIcon />}
                            />
                            <Switch
                                checked={!usdView}
                                size="md"
                                onChange={() => setUsdView(!usdView)}
                                iconOn={<AttachMoneyIcon />} // Icon for USD view
                                iconOff={<CurrencyBitcoinIcon />} // Icon for Crypto view
                            />
                        </div>

                        <div style={{
                            flexDirection: 'column',
                            display: 'grid',
                            flexWrap: 'wrap',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                            justifyContent: 'flex-start',
                            gap: '10px',
                            //marginTop: '10px',
                        }}>
                            {userStakedTokens.length > 0 ? (
                                userStakedTokens.map(({ token, totalShares, userStakedBalance, unclaimed, userShares }) => (
                                    <NextCard key={token.address} style={miniTokenCardStyle(getTokenLogoUrl(token))}
                                        onClick={() => handlePlayPlatformClick(token, 3)}
                                        isHoverable={!detailView} isPressable={!detailView}
                                    >
                                        <NextCard.Body style={{ padding: '3px', overflow: 'auto', position: 'relative' }}>
                                            <div style={{ display: 'flex', alignContent: 'space-between', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>

                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold', fontSize: '18px' }}>
                                                    <img src={getTokenLogoUrl(token)} alt={token.label} style={{ width: '25px' }} />
                                                    <span>{token.label}</span>
                                                </div>
                                                <div
                                                    style={{ fontSize: '18px', display: 'flex', flexDirection: 'row' }}
                                                >
                                                    🥩:<strong> {usdView ? formatBalanceWithIcon(userStakedBalance, token.address, imageReferences, token.decimals) : formatUSDBalanceWithIcon(parseFloat(userStakedBalance) * parseFloat(token.price) / (10 ** token.decimals), token.address, imageReferences)} </strong>
                                                    {detailView &&
                                                        <Button
                                                            label="Unstake"
                                                            onClick={() => handleUnstake(token, userStakedBalance)}
                                                            disabled={txPending}
                                                            severity="secondary"
                                                            rounded
                                                            size="small"
                                                            style={{
                                                                paddingTop: '2px', paddingBottom: '2px', paddingLeft: '5px', paddingRight: '5px', marginLeft: '5px',
                                                            }}
                                                        />}
                                                </div>

                                                {/* <div style={{ fontSize: '16px', fontWeight: 'normal', textAlign: 'right' }}>
                                                {formatCurrencyExtended(parseFloat(userStakedBalance) * parseFloat(token.price) / (10 ** token.decimals))}
                                            </div> */}
                                            </div>
                                            {
                                                ((new BigNumber(unclaimed)).gt(0) || (new BigNumber(userShares.totalRealised)).gt(0)) &&
                                                <div style={{
                                                    fontSize: '14px', fontWeight: 'normal', alignItems: 'center',
                                                    backgroundColor: '#e8f4ff', padding: '6px',
                                                    marginTop: '10px', borderRadius: '15px',
                                                }}>
                                                    Rewards
                                                    <div style={{ fontSize: '14px', color: 'black', display: 'flex', justifyContent: (new BigNumber(unclaimed)).gt(0) ? 'space-between' : 'flex-end', alignItems: 'center' }}>


                                                        {(new BigNumber(unclaimed)).gt(0) &&
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', flexDirection: 'column', }}>
                                                                {!detailView && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', flexDirection: 'row', }}>
                                                                    ⏳:<strong> {usdView ? formatBalanceWithIcon(unclaimed, token.address, imageReferences, token.decimals) : formatUSDBalanceWithIcon(parseFloat(unclaimed) * parseFloat(token.price) / (10 ** token.decimals), token.address, imageReferences)} </strong>

                                                                </div>}
                                                                {detailView &&
                                                                    <Button
                                                                        label={(usdView ? formatBalanceWithIcon(unclaimed, token.address, imageReferences, token.decimals) : formatUSDBalanceWithIcon(parseFloat(unclaimed) * parseFloat(token.price) / (10 ** token.decimals), token.address, imageReferences))}
                                                                        onClick={() => handleClaimAllRewards(token)}
                                                                        disabled={txPending}
                                                                        severity="info"
                                                                        rounded
                                                                        size="small"
                                                                        style={{
                                                                            paddingTop: '2px', paddingBottom: '2px', paddingLeft: '5px', paddingRight: '5px', marginLeft: '5px', marginTop: '5px',
                                                                        }}
                                                                    />}
                                                            </div>
                                                        }
                                                        {(new BigNumber(userShares.totalRealised)).gt(0) &&
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexDirection: 'row', }}>
                                                                📥:<strong> {usdView ? formatBalanceWithIcon(userShares.totalRealised, token.address, imageReferences, token.decimals) : formatUSDBalanceWithIcon(parseFloat(userShares.totalRealised) * parseFloat(token.price) / (10 ** token.decimals), token.address, imageReferences)}  </strong>
                                                            </div>
                                                        }
                                                        {/* <div style={{ display: 'flex', alignSelf: 'flex-end' }}>
                                                        + {formatUSDBalanceWithIcon(parseFloat(unclaimed) * parseFloat(token.price) / (10 ** token.decimals), token.address, imageReferences)}
                                                    </div> */}
                                                    </div>
                                                    {/* {(new BigNumber(userShares.totalRealised)).gt(0) &&
                                                <div style={{ fontSize: '14px', color: 'black', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexDirection: 'row', }}>
                                                        📥:<strong> {usdView ? formatBalanceWithIcon(userShares.totalRealised, token.address, imageReferences, token.decimals) : formatUSDBalanceWithIcon(parseFloat(userShares.totalRealised) * parseFloat(token.price) / (10 ** token.decimals), token.address, imageReferences)}  </strong>
                                                    </div>
                                                </div>} */}
                                                </div>
                                            }

                                            <div style={{
                                                fontSize: '12px', alignItems: 'center', display: 'flex', justifyContent: 'space-between', marginTop: '15px', marginBottom: '0px',
                                                // backgroundColor: '#256ba8', color: 'white', padding: '5px',
                                                // borderBottomLeftRadius: '5px', borderBottomRightRadius: '5px',
                                            }}>

                                                <div style={{
                                                    fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'row'
                                                    //backgroundColor: '#003059', color: 'white', 
                                                    //padding: '6px', borderRadius: '15px'
                                                }}>
                                                    ⚖️: {(userStakedBalance.div(totalShares).multipliedBy(100)).toFixed(2)}%
                                                </div>
                                                <div style={{
                                                    fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'row'
                                                    //backgroundColor: '#003059', color: 'white', 
                                                    // padding: '6px', borderRadius: '15px'
                                                }}>
                                                    TVL 🥩: {usdView ? formatBalanceWithIcon(totalShares, token.address, imageReferences, token.decimals) : formatUSDBalanceWithIcon(parseFloat(totalShares) * parseFloat(token.price) / (10 ** token.decimals), token.address, imageReferences)}
                                                </div>
                                            </div>
                                            <div style={{
                                                fontSize: '12px', alignItems: 'center', display: 'flex', justifyContent: 'space-between', marginBottom: '0px',
                                                // backgroundColor: '#256ba8', color: 'white', padding: '5px',
                                                // borderBottomLeftRadius: '5px', borderBottomRightRadius: '5px',
                                            }}>
                                                <div style={{
                                                    fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'row'
                                                    //backgroundColor: '#003059', color: 'white', 
                                                    // padding: '6px', borderRadius: '15px'
                                                }}>
                                                    📈: {usdView ? formatBalanceWithIcon(totalShares * 0.45, token.address, imageReferences, token.decimals) : formatUSDBalanceWithIcon(parseFloat(totalShares) * 0.45 * parseFloat(token.price) / (10 ** token.decimals), token.address, imageReferences)} /mo
                                                </div>
                                                <div style={{
                                                    fontSize: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'row'
                                                    //backgroundColor: '#003059', color: 'white', 
                                                    //padding: '6px', borderRadius: '15px'
                                                }}>
                                                    APY 🏛️: {(userStakedBalance.div(totalShares).multipliedBy(100) * 0.4).toFixed(2)}%
                                                </div>
                                            </div>

                                        </NextCard.Body>
                                    </NextCard>
                                ))
                            ) : (
                                <p>No tokens staked</p>
                            )}
                        </div>

                        {showPlayPlatformModal && (
                            <PlayPlatformModal
                                playPlatform={selectedPlayPlatform}
                                token={selectedToken}
                                isVisible={showPlayPlatformModal}
                                setShowModal={setShowPlayPlatformModal}
                                getTokenLogoUrl={getTokenLogoUrl}
                                poolType="Stake"
                                onQuickPlay={handleQuickStakePlay}
                                txPending={txPending}
                                needsApproval={allowance.lt(new BigNumber(amountIn))}
                                onApproveToken={approveToken}
                                fetchPlayPlatform={fetchPlayPlatform}  // Pass the refetch function
                            />
                        )}
                    </TabPanel>
                </TabView>
            </div>
        </div>
    );

});





const Burn = React.memo(({ tokenSelections, refreshTokenSelections, imageReferences }) => {
    const { account, library } = useWeb3React();
    const contractContext = useContext(ContractContext);
    const contracts = contractContext.contracts;

    const [tokenIn, setTokenIn] = useState(null);
    const [tokenInBalance, setTokenInBalance] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [filteredTokens, setFilteredTokens] = useState([]);
    const [requiredAmount, setRequiredAmount] = useState(new BigNumber(0));
    const [allowance, setAllowance] = useState(new BigNumber(0));
    const [txPending, setTxPending] = useState(false);

    const [userBurnTokens, setUserBurnTokens] = useState([]);

    const [showPlayPlatformModal, setShowPlayPlatformModal] = useState(false);
    const [selectedPlayPlatform, setSelectedPlayPlatform] = useState(null);
    const [selectedToken, setSelectedToken] = useState(null);

    // Remove PrimeReact's Toast and react-toastify's toast ref
    // const toast = React.useRef(null); // Removed

    const handleTokenSelectByType = useCallback((type, token) => {
        if (type === 'in') {
            setTokenIn(token);
            localStorage.setItem('tokenInBurn', token.address);
        }
    }, []);

    const fetchPlayPlatform = useCallback(async (token) => {
        try {
            const playPlatform = await contracts.floatiePlay.methods.playPlatform(token.address, 4).call();
            setSelectedPlayPlatform(playPlatform);
        } catch (error) {
            console.error("Error fetching play platform:", error);
        }
    }, [contracts.floatiePlay]);

    useEffect(() => {
        const fetchRequiredAmount = async () => {
            try {
                if (!tokenIn || !contracts.floatiePlay) return;
                const playPlatformSettings = await contracts.floatiePlay.methods.playPlatform(tokenIn.address, 4).call(); // 4 is the poolIndex for Burn
                setRequiredAmount(new BigNumber(playPlatformSettings.poolSettings.requiredAmount));
            } catch (error) {
                console.error("Error fetching required amount:", error);
            }
        };

        if (tokenIn) {
            fetchRequiredAmount();
            fetchAllowance(tokenIn); // Also fetch the allowance for this token
        }
    }, [tokenIn, contracts.floatiePlay, tokenSelections]);

    const handlePlayPlatformClick = useCallback(async (token, poolIndex) => {
        try {
            await fetchPlayPlatform(token);
            setSelectedToken(token);
            setShowPlayPlatformModal(true);
        } catch (error) {
            console.error("Error fetching play platform:", error);
        }
    }, [contracts.floatiePlay]);

    const fetchAllowance = useCallback(async (token) => {
        try {
            const web3 = new Web3(library.provider);
            const tokenContract = new web3.eth.Contract(ierc20Abi, token.address);
            const allowance = await tokenContract.methods.allowance(account, contracts.floatieGateway._address).call();
            setAllowance(new BigNumber(allowance));
        } catch (error) {
            console.error("Error fetching allowance:", error);
        }
    }, [account, library, contracts.floatieGateway]);

    const [playPlatform, setPlayPlatform] = useState(null);

    useEffect(() => {
        if (playPlatform) {
            console.log("Play Platform: ", playPlatform);
        }
    }, [playPlatform]);

    const getTokenPlayPlatformByIndex = useCallback(async (token, poolIndex) => {
        // playPlatform structs
        // struct WinnerInfo {
        //     uint256 previousWonAmount;
        //     uint256 previousWinTime;
        //     address previousWinner;
        // }

        // struct PoolSettings {
        //     uint256 chance;             
        //     uint256 accuracy;          
        //     uint256 rewardRate; //to be divided by 10000
        //     uint256 requiredAmount;
        //     uint256 threshold;
        //     uint256 coolDownTime;
        //     uint256 minimumHoldAmount;
        // }

        // struct PlayPlatform {
        //     uint256 total;
        //     uint256 lastRoll;
        //     PoolSettings poolSettings;
        //     WinnerInfo winnerInfo;
        // } 
        try {
            if (!contracts.floatiePlay) return;
            const playPlatform = await contracts.floatiePlay.methods.playPlatform(token.address, poolIndex).call();
            setPlayPlatform(playPlatform);
        } catch (error) {
            console.error("Error fetching play platform:", error);
        }
    }, [contracts.floatiePlay]);

    const fetchBurnedTokens = useCallback(async () => {
        try {
            const totalSharesData = await Promise.all(
                tokenSelections
                    .filter(token => token.balanceOfHolder > 0)
                    .map(async (token) => {
                        const totalShares = new BigNumber(await contracts.floatieBurn.methods.getTotalShares(token.address).call());
                        const userShares = await contracts.floatieBurn.methods.floatieBurners(account, token.address).call();
                        console.log("User Shares: ", userShares);
                        const userBurnedBalance = new BigNumber(userShares.totalBurned);
                        const unclaimed = (new BigNumber(await contracts.floatieBurn.methods.getUnpaidEarnings(token.address, account).call())).plus(new BigNumber(userShares.totalRealisedUndeposited));
                        console.log(`Token: ${token.label}, Total Shares: ${totalShares.toString()}, User Burned: ${userBurnedBalance.toString()}`);
                        return {
                            token,
                            totalShares,
                            userBurnedBalance: userBurnedBalance.isNaN() ? new BigNumber(0) : userBurnedBalance,
                            unclaimed,
                            userShares,
                        };
                    })
            );

            const filteredTokensWithActiveBurns = totalSharesData.filter(({ totalShares, userBurnedBalance }) => totalShares.gt(0) && userBurnedBalance.gt(0));

            setUserBurnTokens(filteredTokensWithActiveBurns);
        } catch (error) {
            console.error("Error fetching burned tokens:", error);
        }
    }, [tokenSelections, contracts.floatieBurn, account]);

    // useEffect(() => {
    //     return () => {
    //         setUserBurnTokens([]); // Clear the state when the component is unmounted or switching tabs
    //     };
    // }, []);

    useEffect(() => {
        if (account && contracts.floatieBurn) {
            fetchBurnedTokens();
        }
    }, [account, contracts.floatieBurn, tokenSelections, fetchBurnedTokens]);

    useEffect(() => {
        if (!tokenSelections || tokenSelections.length === 0) return;
        const storedTokenIn = localStorage.getItem('tokenInBurn');

        const foundTokenIn = tokenSelections.find(token => token.address === storedTokenIn);

        if (foundTokenIn) {
            setTokenIn(foundTokenIn);
        } else {
            // Fallback to default tokens if not found in storage or token list
            const defaultTokenIn = tokenSelections.find(token => token.address === '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c');
            setTokenIn(defaultTokenIn);
        }
    }, [tokenSelections]);

    const getTokenLogoUrl = (token) => {
        return getTokenLogo(imageReferences, token);
    };

    const getTokenBalance = useCallback(async (token) => {
        try {
            if (!library?.provider || !token) return 0;
            const web3 = new Web3(library.provider);
            if (token.label === 'WBNB') {
                const balance = await web3.eth.getBalance(account);
                return balance;
            } else {
                const contract = new web3.eth.Contract(ierc20Abi, token.address);
                const balance = await contract.methods.balanceOf(account).call();
                return balance;
            }
        } catch (error) {
            console.error("Error fetching token balance:", error);
            return 0;
        }
    }, [account, library]);

    useEffect(() => {
        if (tokenIn && tokenIn.address) {
            getTokenBalance(tokenIn).then(balance => setTokenInBalance(balance));
            fetchAllowance(tokenIn);
        } else {
            setTokenInBalance(0);
        }
    }, [tokenIn, account, library, getTokenBalance, fetchAllowance]);

    const approveToken = useCallback(async (token) => {
        try {
            setTxPending(true); // Disable buttons
            const web3 = new Web3(library.provider);
            const tokenContract = new web3.eth.Contract(ierc20Abi, token.address);
            const amountWithDecimals = new BigNumber(requiredAmount).toFixed().toString();

            // Show initial loading toast with react-toastify
            const toastId = toast.loading("Approving token...");

            await tokenContract.methods.approve(contracts.floatieGateway._address, amountWithDecimals).send({ from: account })
                .on('transactionHash', (hash) => {
                    // Update the toast with TransactionToast component
                    toast.update(toastId, {
                        render: <TransactionToast hash={hash} transactionType="Approval" />, // Pass transactionType
                        type: "info",
                        isLoading: true, // Keep the spinner
                        autoClose: false, // Prevent auto-close
                        closeButton: true,
                    });
                })
                .on('receipt', (receipt) => {
                    // Update the toast to success and remove the spinner
                    toast.update(toastId, {
                        render: "Token approved successfully!",
                        type: "success",
                        isLoading: false, // Remove spinner
                        autoClose: 3000, // Auto close after 3 seconds
                        closeButton: true,
                    });
                    setTxPending(false); // Enable buttons
                })
                .on('error', (error) => {
                    // Update the toast to error and remove the spinner
                    toast.update(toastId, {
                        render: `Approval failed: ${error.message}`,
                        type: "error",
                        isLoading: false, // Remove spinner
                        autoClose: 5000, // Auto close after 5 seconds
                        closeButton: true,
                    });
                    setTxPending(false); // Enable buttons
                });

            await fetchAllowance(token); // Fetch updated allowance after approval
        } catch (error) {
            console.error("Error approving token:", error);
            //toast.error(`Approval Error: ${error.message}`);
            setTxPending(false); // Enable buttons
        }
    }, [account, requiredAmount, library, contracts.floatieGateway, fetchAllowance]);

    const handleBurn = async () => {
        try {
            setTxPending(true); // Disable buttons

            // Show initial loading toast with react-toastify
            const toastId = toast.loading("Burning tokens...");

            await contracts.floatieGateway.methods.burnPlay(tokenIn.address, false).send({ from: account }) // false for not using staked balance
                .on('transactionHash', (hash) => {
                    // Update the toast with TransactionToast component
                    toast.update(toastId, {
                        render: <TransactionToast hash={hash} transactionType="Burning" />, // Pass transactionType
                        type: "info",
                        isLoading: true, // Keep the spinner
                        autoClose: false, // Prevent auto-close
                        closeButton: true,
                    });
                })
                .on('receipt', (receipt) => {
                    // Update the toast to success and remove the spinner
                    toast.update(toastId, {
                        render: "Tokens burned successfully!",
                        type: "success",
                        isLoading: false, // Remove spinner
                        autoClose: 3000, // Auto close after 3 seconds
                        closeButton: true,
                    });
                    setTxPending(false); // Enable buttons
                })
                .on('error', (error) => {
                    // Update the toast to error and remove the spinner
                    toast.update(toastId, {
                        render: `Burn failed: ${error.message}`,
                        type: "error",
                        isLoading: false, // Remove spinner
                        autoClose: 5000, // Auto close after 5 seconds
                        closeButton: true,
                    });
                    setTxPending(false); // Enable buttons
                });
        } catch (error) {
            console.error('Transaction failed:', error);
            //toast.error(`The transaction has failed. ${error.message}`);
            setTxPending(false); // Enable buttons
        }
        refreshTokenSelections();
    };

    const handleQuickBurnPlay = async (token, poolSettings) => {
        try {
            setTxPending(true); // Disable buttons

            // Perform burn logic
            const burnAmount = new BigNumber(poolSettings.requiredAmount).toFixed().toString();

            // Check allowance
            await fetchAllowance(token);
            if (allowance.lt(burnAmount)) {
                await approveToken(token);
            }

            // Show initial loading toast with react-toastify
            const toastId = toast.loading("Burning tokens...");

            await contracts.floatieGateway.methods.burnPlay(token.address, false).send({ from: account })
                .on('transactionHash', (hash) => {
                    // Update the toast with TransactionToast component
                    toast.update(toastId, {
                        render: <TransactionToast hash={hash} transactionType="Burning" />, // Pass transactionType
                        type: "info",
                        isLoading: true, // Keep the spinner
                        autoClose: false, // Prevent auto-close
                        closeButton: true,
                    });
                })
                .on('receipt', (receipt) => {
                    // Update the toast to success and remove the spinner
                    toast.update(toastId, {
                        render: "Tokens burned successfully!",
                        type: "success",
                        isLoading: false, // Remove spinner
                        autoClose: 3000, // Auto close after 3 seconds
                        closeButton: true,
                    });
                    setTxPending(false); // Enable buttons
                })
                .on('error', (error) => {
                    // Update the toast to error and remove the spinner
                    toast.update(toastId, {
                        render: `Burn failed: ${error.message}`,
                        type: "error",
                        isLoading: false, // Remove spinner
                        autoClose: 5000, // Auto close after 5 seconds
                        closeButton: true,
                    });
                    setTxPending(false); // Enable buttons
                });

        } catch (error) {
            console.error('Burn failed:', error);
            //toast.error(`The transaction has failed. ${error.message}`);
            setTxPending(false); // Enable buttons
        }
        refreshTokenSelections();
        fetchBurnedTokens();
        fetchPlayPlatform(token);
    };

    const handleTokenClick = useCallback(() => {
        setShowModal(true);
        setFilteredTokens(tokenSelections.filter(t => t.address.toLowerCase() !== tokenIn?.address.toLowerCase()));
    }, [tokenIn, tokenSelections]);

    const handleTokenSelect = useCallback((token) => {
        setTokenIn(token);
        setShowModal(false);
        localStorage.setItem('tokenInBurn', token.address);
    }, []);

    const handleCloseModal = useCallback(() => {
        setShowModal(false);
    }, []);

    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Function to determine if the screen is in desktop mode
    const isDesktop = () => windowWidth >= 768; // Adjust the breakpoint as needed

    return (
        <div style={{
            backgroundImage: 'linear-gradient(315deg, #E5F3FF 0%, #E5F3FF 0%)',
            borderRadius: '25px', paddingBottom: '10px', paddingTop: '5px',
            marginTop: '0px', marginBottom: '10px', overflowX: 'hidden',
        }}>
            <div style={containerStyle}>
                <div style={{
                    display: 'flex', // Use flexbox for layout
                    alignItems: 'center', // Center items vertically
                    justifyContent: 'center', // Center the logo horizontally
                    position: 'relative', // Needed for absolute positioning of the settings button
                    ...infoOutlineStyle, // Keep your existing styles
                    paddingTop: '0px', paddingBottom: '10px', paddingLeft: '5px', paddingRight: '5px',
                }}>
                    <img src={burnlab_logo} alt="burnlab logo" style={{ maxWidth: '250px', maxHeight: '43px', marginBottom: '15px', marginTop: '25px', paddingTop: '5px' }} />
                </div>
                <TabView>
                    <TabPanel header="BurnPlay">
                        <div style={{
                            marginRight: isDesktop() ? '-50%' : '-40px',
                            marginLeft: isDesktop() ? '-50%' : '-40px',
                            marginBottom: '20px',
                        }}>
                            {tokenSelections?.length > 0 ? (
                                <TokenCarousel
                                    tokens={tokenSelections}
                                    selectedTokenIn={tokenIn}
                                    onSelectToken={handleTokenSelectByType}
                                    getTokenLogoUrl={getTokenLogoUrl}
                                    selectionType="in"  // Only tokenIn will be updated in Burn
                                    poolIndex={4} // Pass the relevant pool index for Burn
                                />
                            ) : (
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <SkeletonCard />
                                    <SkeletonCard />
                                    <SkeletonCard />
                                    <SkeletonCard />
                                    <SkeletonCard />
                                    <SkeletonCard />
                                    <SkeletonCard />
                                    <SkeletonCard />
                                    <SkeletonCard />
                                    <SkeletonCard />
                                    <SkeletonCard />
                                </div>
                            )}
                        </div>
                        <div style={{ marginTop: '15px' }}>
                            <TokenInput
                                onSelectToken={handleTokenClick}
                                token={tokenIn}
                                balance={tokenInBalance}
                                getTokenLogoUrl={getTokenLogoUrl}
                                //set amountIn to min required burn amount
                                amountIn={requiredAmount.toString()}
                                readOnly={true}
                            />
                        </div>
                        <RewardsCard
                            token={tokenIn}
                            imageReferences={imageReferences}
                            getTokenLogoUrl={getTokenLogoUrl}
                            poolIndex={4} // Pass the relevant pool index for Burn
                            poolType="Burn"
                        />
                        {allowance.lt(requiredAmount) ? (
                            <Button label="Approve" onClick={() => approveToken(tokenIn)}
                                severity="info"
                                disabled={txPending || !tokenIn}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    height: '50px',
                                    borderRadius: '25px',
                                    marginTop: '15px', marginBottom: '15px',
                                }} />
                        ) : (
                            <Button label="Burn" onClick={handleBurn}
                                disabled={!tokenIn || txPending}
                                severity="danger"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center', // Center the contents
                                    gap: '10px',
                                    width: '100%', // Minimum width for each button
                                    height: '50px',
                                    borderRadius: '25px',
                                    marginTop: '15px', marginBottom: '15px',
                                }}
                            />
                        )}
                    </TabPanel>
                    <TabPanel header="Your Burned">

                        <div style={{
                            flexDirection: 'column',
                            display: 'grid',
                            flexWrap: 'wrap',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                            justifyContent: 'flex-start',
                            gap: '10px',
                            //marginTop: '10px',
                        }}>
                            {userBurnTokens.length > 0 ? (
                                userBurnTokens.map(({ token, totalShares, userBurnedBalance, unclaimed, userShares }) => (
                                    <NextCard key={token.address} style={miniTokenCardStyle(getTokenLogoUrl(token))}
                                        onClick={() => handlePlayPlatformClick(token, 4)} // 4 is the poolIndex for Burn
                                        isHoverable isPressable
                                    >
                                        <NextCard.Body style={{ padding: '3px', overflow: 'auto', position: 'relative' }}>
                                            <div style={{ display: 'flex', alignContent: 'space-between', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                                                <div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold', fontSize: '18px' }}>
                                                        <img src={getTokenLogoUrl(token)} alt={token.label} style={{ width: '25px' }} />
                                                        <span>{token.label}</span>
                                                    </div>
                                                    <div>🔥Burned: {formatDecimals(userBurnedBalance, token.decimals)}</div>
                                                </div>
                                                <div style={{ fontSize: '16px', fontWeight: 'normal', textAlign: 'right' }}>
                                                    {formatCurrencyExtended(parseFloat(userBurnedBalance) * parseFloat(token.price) / (10 ** token.decimals))}
                                                </div>
                                            </div>
                                            {(new BigNumber(unclaimed)).gt(0) &&
                                                <div style={{ fontSize: '12px', color: 'black', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div>
                                                        Pending: +<strong>{formatDecimals(unclaimed, token.decimals)} {token.label}</strong>
                                                    </div>
                                                    <div>
                                                        + {formatCurrencyExtended(parseFloat(unclaimed) * parseFloat(token.price) / (10 ** token.decimals))} USD
                                                    </div>
                                                </div>}
                                            {(new BigNumber(userShares.totalRealised)).gt(0) &&
                                                <div style={{ fontSize: '12px', color: 'black', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <div>
                                                        Claimed: <strong>{formatDecimals(userShares.totalRealised, token.decimals)} {token.label}</strong>
                                                    </div>
                                                    <div>
                                                        + {formatCurrencyExtended(parseFloat(userShares.totalRealised) * parseFloat(token.price) / (10 ** token.decimals))} USD
                                                    </div>
                                                </div>}
                                            <div style={{ fontSize: '12px', fontWeight: 'normal', display: 'inline-block' }}>
                                                ⛽ Reward Position: {(userBurnedBalance.div(totalShares).multipliedBy(100)).toFixed(2)}%
                                            </div>
                                        </NextCard.Body>
                                    </NextCard>
                                ))
                            ) : (
                                <p>No tokens burned</p>
                            )}
                        </div>
                        {showPlayPlatformModal && (
                            <PlayPlatformModal
                                playPlatform={selectedPlayPlatform}
                                token={selectedToken}
                                isVisible={showPlayPlatformModal}
                                setShowModal={setShowPlayPlatformModal}
                                getTokenLogoUrl={getTokenLogoUrl}
                                poolType="Burn"
                                onQuickPlay={handleQuickBurnPlay}
                                txPending={txPending}  // Pass txPending state
                                needsApproval={allowance.lt(requiredAmount)}  // Check if approval is needed
                                onApproveToken={approveToken}  // Pass the approve function
                                fetchPlayPlatform={fetchPlayPlatform}  // Pass the refetch function
                            />
                        )}
                    </TabPanel>
                </TabView>

                {showModal && (
                    <TokenSelectorModal
                        tokens={filteredTokens}
                        onSelect={handleTokenSelect}
                        onClose={handleCloseModal}
                        isVisible={showModal}
                        setShowModal={setShowModal}
                        getTokenLogoUrl={getTokenLogoUrl}
                        formatBalanceWithLogo={formatBalanceWithIcon}
                        poolIndex={4} // Pass the relevant pool index for Burn
                        poolType="Burn"
                        txPending={txPending}
                    />
                )}
            </div>
            {/* Remove PrimeReact's Toast */}
            {/* <Toast ref={toast} /> */} {/* Removed */}
        </div>
    );
});


const Pay = React.memo(({ tokenSelections, imageReferences, refreshTokenSelections }) => {
    const { account, library } = useWeb3React();
    const contractContext = useContext(ContractContext);
    const contracts = contractContext.contracts;

    const [tokenIn, setTokenIn] = useState(null);
    const [tokenInBalance, setTokenInBalance] = useState(0);
    const [amountIn, setAmountIn] = useState('');
    const [recipient, setRecipient] = useState('');
    const [recipientSummary, setRecipientSummary] = useState(null); // For recipient summary card
    const [showModal, setShowModal] = useState(false);
    const [filteredTokens, setFilteredTokens] = useState([]);
    const [txPending, setTxPending] = useState(false);
    const [allowance, setAllowance] = useState(new BigNumber(0));

    const toast = React.useRef(null);

    useEffect(() => {
        if (!tokenSelections || tokenSelections.length === 0) return;
        const storedTokenIn = localStorage.getItem('tokenInPay');

        const foundTokenIn = tokenSelections.find(token => token.address === storedTokenIn);

        if (foundTokenIn) {
            setTokenIn(foundTokenIn);
        } else {
            const defaultTokenIn = tokenSelections.find(token => token.address === '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c');
            setTokenIn(defaultTokenIn);
        }
    }, [tokenSelections]);

    const getTokenLogoUrl = (token) => {
        return getTokenLogo(imageReferences, token);
    };

    const getTokenBalance = useCallback(async (token) => {
        try {
            if (!library?.provider || !token) return 0;
            const web3 = new Web3(library.provider);
            if (token.label === 'WBNB') {
                const balance = await web3.eth.getBalance(account);
                return balance;
            } else {
                const contract = new web3.eth.Contract(ierc20Abi, token.address);
                const balance = await contract.methods.balanceOf(account).call();
                return balance;
            }
        } catch (error) {
            console.error("Error fetching token balance:", error);
            return 0;
        }
    }, [account, library]);

    useEffect(() => {
        if (tokenIn && tokenIn.address) {
            getTokenBalance(tokenIn).then(balance => setTokenInBalance(balance));
            fetchAllowance(tokenIn);
        } else {
            setTokenInBalance(0);
        }
    }, [tokenIn, account, library]);

    const fetchAllowance = useCallback(async (token) => {
        try {
            const web3 = new Web3(library.provider);
            const tokenContract = new web3.eth.Contract(ierc20Abi, token.address);
            const allowance = await tokenContract.methods.allowance(account, contracts.floatieGateway._address).call();
            setAllowance(new BigNumber(allowance));
        } catch (error) {
            console.error("Error fetching allowance:", error);
        }
    }, [account, library, contracts.floatieGateway]);

    const approveToken = useCallback(async (token) => {
        try {
            setTxPending(true);
            const web3 = new Web3(library.provider);
            const tokenContract = new web3.eth.Contract(ierc20Abi, token.address);
            const amountWithDecimals = new BigNumber(amountIn).toFixed().toString();

            const txToast = toast.current;
            txToast.clear();
            txToast.show({ severity: 'info', summary: 'Transaction Submitted', detail: 'Approving token...', sticky: true });

            await tokenContract.methods.approve(contracts.floatieGateway._address, amountWithDecimals).send({ from: account })
                .on('transactionHash', (hash) => {
                    txToast.clear();
                    txToast.show({ severity: 'info', summary: 'Transaction Hash', detail: `Waiting for confirmation. Hash: ${hash}`, sticky: true });
                })
                .on('receipt', (receipt) => {
                    txToast.clear();
                    txToast.show({ severity: 'success', summary: 'Approval Success', detail: 'Tokens approved for payment', life: 3000 });
                    setTxPending(false);
                })
                .on('error', (error) => {
                    txToast.clear();
                    txToast.show({ severity: 'error', summary: 'Transaction Failed', detail: error.message, life: 5000 });
                    setTxPending(false);
                });

            await fetchAllowance(token);
        } catch (error) {
            console.error("Approval error:", error);
            toast.current.show({ severity: 'error', summary: 'Approval Error', detail: error.message, life: 3000 });
            setTxPending(false);
        }
    }, [account, amountIn, library, contracts.floatieGateway, fetchAllowance]);

    const handlePay = async () => {
        try {
            if (!tokenIn || !amountIn || !recipient) {
                toast.current.show({ severity: 'warn', summary: 'Incomplete Data', detail: 'Please provide all required inputs.', life: 3000 });
                return;
            }

            const amountWithDecimals = new BigNumber(amountIn).times(10 ** tokenIn.decimals).toFixed(0).toString();

            setTxPending(true);

            await contracts.floatieGateway.methods.earnPay(recipient, tokenIn.address, amountWithDecimals, false, false).send({ from: account })
                .on('transactionHash', (hash) => {
                    toast.current.show({ severity: 'info', summary: 'Transaction Submitted', detail: `Transaction hash: ${hash}`, life: 3000 });
                })
                .on('receipt', (receipt) => {
                    toast.current.show({ severity: 'success', summary: 'Payment Success', detail: `Paid ${amountIn} ${tokenIn.label} to ${recipient}`, life: 3000 });
                })
                .on('error', (error) => {
                    toast.current.show({ severity: 'error', summary: 'Transaction Failed', detail: error.message, life: 5000 });
                });

            setTxPending(false);
        } catch (error) {
            console.error("Payment error:", error);
            toast.current.show({ severity: 'error', summary: 'Payment Error', detail: error.message, life: 3000 });
            setTxPending(false);
        }
    };

    const handleRecipientChange = (e) => {
        const value = e.target.value;
        setRecipient(value);

        // Simulate fetching smart code/address (can be an async function to get real data)
        if (value === "someSmartCode123") {
            setRecipientSummary({
                smartCode: value,
                address: "0x123...abc" // mock address associated with this smart code
            });
        } else if (value.startsWith("0x")) {
            setRecipientSummary({
                smartCode: null,
                address: value
            });
        } else {
            setRecipientSummary(null);
        }
    };

    const isDesktop = () => window.innerWidth >= 768;

    return (
        <div style={{
            backgroundImage: 'linear-gradient(315deg, #E5F3FF 0%, #E5F3FF 0%)',
            borderRadius: '25px', paddingBottom: '10px', paddingTop: '5px',
            marginTop: '0px', marginBottom: '10px', overflowX: 'hidden',
        }}>
            <div style={containerStyle}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    paddingTop: '0px', paddingBottom: '10px', paddingLeft: '5px', paddingRight: '5px',
                }}>
                    <img src={earnpay_logo} alt="earnpay logo" style={{ maxWidth: '250px', maxHeight: '50px', marginBottom: '15px', marginTop: '25px', paddingTop: '5px' }} />
                </div>

                <div style={{
                    marginRight: isDesktop() ? '-50%' : '-40px',
                    marginLeft: isDesktop() ? '-50%' : '-40px',
                    marginBottom: '20px',
                }}>
                    {tokenSelections?.length > 0 ? (
                        <TokenCarousel
                            tokens={tokenSelections}
                            selectedTokenIn={tokenIn}
                            onSelectToken={(type, token) => setTokenIn(token)}
                            getTokenLogoUrl={getTokenLogoUrl}
                            selectionType="in"
                            poolIndex={2}
                        />
                    ) : (
                        <div>No tokens available</div>
                    )}
                </div>

                <TokenInput
                    onSelectToken={() => setShowModal(true)}
                    token={tokenIn}
                    balance={tokenInBalance}
                    amountIn={amountIn}
                    onAmountChange={setAmountIn}
                    getTokenLogoUrl={getTokenLogoUrl}
                />

                <InputText
                    value={recipient}
                    onChange={handleRecipientChange}
                    placeholder="Enter recipient address or smart code"
                    style={{ width: '100%', marginBottom: '20px', borderRadius: '25px' }}
                />

                {recipientSummary && (
                    <div style={{ marginBottom: '20px', padding: '10px', background: '#f7f7f7', borderRadius: '10px' }}>
                        <strong>Recipient Summary:</strong>
                        <p>Smart Code: {recipientSummary.smartCode || 'N/A'}</p>
                        <p>Address: {recipientSummary.address}</p>
                    </div>
                )}

                <RewardsCard
                    token={tokenIn}
                    imageReferences={imageReferences}
                    getTokenLogoUrl={getTokenLogoUrl}
                    poolIndex={2}
                    poolType="Pay"
                />

                {allowance.lt(new BigNumber(amountIn)) ? (
                    <Button label="Approve" onClick={() => approveToken(tokenIn)}
                        disabled={!tokenIn || !amountIn || amountIn <= 0 || txPending}
                        severity="info"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '50px',
                            borderRadius: '25px',
                            marginTop: '15px', marginBottom: '15px',
                        }} />
                ) : (
                    <Button label="Pay" onClick={handlePay}
                        disabled={!tokenIn || !amountIn || amountIn <= 0 || !recipient || txPending}
                        severity="warning"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px',
                            width: '100%',
                            height: '50px',
                            borderRadius: '25px',
                            marginTop: '15px', marginBottom: '15px',
                        }}
                    />
                )}

                {showModal && (
                    <TokenSelectorModal
                        tokens={filteredTokens}
                        onSelect={setTokenIn}
                        onClose={() => setShowModal(false)}
                        isVisible={showModal}
                        getTokenLogoUrl={getTokenLogoUrl}
                        poolIndex={2}
                        poolType="Pay"
                    />
                )}
            </div>
            <Toast ref={toast} />
        </div>
    );
});

export { Pay, Burn, Stake };
